import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Descriptions,
  Tag,
  Table,
  Typography,
  Space,
  Divider,
  Button,
  Spin,
} from "antd";
import {
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { Title, Text } = Typography;

const AssignmentDetailsDrawer = ({
  visible,
  onClose,
  assignment,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const [assignmentDetails, setAssignmentDetails] = useState(null);
  const [templateItems, setTemplateItems] = useState([]);
  const [templateData, setTemplateData] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);

  // Fetch full assignment details and template items when drawer opens
  useEffect(() => {
    const fetchData = async () => {
      if (!assignment || !assignment.id) return;

      setLoadingItems(true);
      try {
        // Fetch full assignment details (includes selections)
        const assignmentResponse = await Utilities.fetchWithAuth(
          "GET",
          domainSettings.urls.bot.clearanceAssignmentDetail.replace(
            ":id",
            assignment.id,
          ),
          null,
          navigate,
        );

        if (assignmentResponse.ok) {
          setAssignmentDetails(assignmentResponse.data);
        }

        // Fetch template items if we have the template ID
        const templateId =
          assignment.requirement_template ||
          assignmentResponse.data?.requirement_template;
        if (templateId) {
          const templateResponse = await Utilities.fetchWithAuth(
            "GET",
            domainSettings.urls.bot.requirementTemplateDetail.replace(
              ":id",
              templateId,
            ),
            null,
            navigate,
          );

          if (templateResponse.ok) {
            setTemplateData(templateResponse.data);
            setTemplateItems(templateResponse.data.requirement_items || []);
          }
        }
      } catch (error) {
        console.error("Error fetching assignment details:", error);
      } finally {
        setLoadingItems(false);
      }
    };

    if (visible && assignment) {
      fetchData();
    } else {
      // Reset state when drawer closes
      setAssignmentDetails(null);
      setTemplateData(null);
      setTemplateItems([]);
    }
  }, [visible, assignment, navigate]);

  // Use the detailed assignment data if available, otherwise fall back to the prop
  const displayAssignment = assignmentDetails || assignment;

  if (!assignment) return null;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  };

  const getStatusColor = (status) => {
    const statusColors = {
      "Pending Selection": "default",
      "Items Selected": "processing",
      "Pending Payment": "warning",
      "Partially Paid": "orange",
      Paid: "success",
      "Ready for Pickup": "purple",
      "Partially Collected": "cyan",
      Collected: "blue",
      Cleared: "green",
      Cancelled: "error",
    };
    return statusColors[status] || "default";
  };

  // Merge template items with selections to show all items with status
  const mergedItems = templateItems.map((templateItem) => {
    // Find if this item has been selected - check multiple possible field names
    const selections =
      displayAssignment.selections || displayAssignment.items || [];
    const selection = selections.find(
      (sel) =>
        sel.requirement_item === templateItem.id ||
        sel.requirement_item_id === templateItem.id ||
        sel.item_id === templateItem.item ||
        sel.item === templateItem.item,
    );

    return {
      id: templateItem.id,
      item_name: templateItem.item_name,
      required_quantity: templateItem.quantity || 1,
      is_selected: !!selection,
      selected_brand:
        selection?.brand_name || selection?.selected_brand || null,
      selected_size: selection?.size_name || selection?.selected_size || null,
      selected_quantity:
        selection?.quantity || selection?.selected_quantity || 0,
      unit_price: selection?.unit_price || 0,
      total_price:
        selection?.total_price ||
        selection?.unit_price * (selection?.quantity || 0) ||
        0,
    };
  });

  // Items table columns showing all available items with selection status
  const itemsColumns = [
    {
      title: "Status",
      key: "status",
      width: 80,
      align: "center",
      render: (_, record) =>
        record.is_selected ? (
          <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "18px" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#d9d9d9", fontSize: "18px" }} />
        ),
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
    },
    {
      title: "Required Qty",
      dataIndex: "required_quantity",
      key: "required_quantity",
      align: "center",
    },
    {
      title: "Selected Brand",
      dataIndex: "selected_brand",
      key: "selected_brand",
      render: (text, record) =>
        record.is_selected ? (
          text || "N/A"
        ) : (
          <Text type="secondary">Not selected</Text>
        ),
    },
    {
      title: "Selected Size",
      dataIndex: "selected_size",
      key: "selected_size",
      render: (text, record) =>
        record.is_selected ? (
          text || "N/A"
        ) : (
          <Text type="secondary">Not selected</Text>
        ),
    },
    {
      title: "Selected Qty",
      dataIndex: "selected_quantity",
      key: "selected_quantity",
      align: "center",
      render: (qty, record) =>
        record.is_selected ? qty : <Text type="secondary">-</Text>,
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (price, record) =>
        record.is_selected ? (
          `UGX ${parseFloat(price || 0).toFixed(2)}`
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Total Price",
      dataIndex: "total_price",
      key: "total_price",
      render: (price, record) =>
        record.is_selected ? (
          <Text strong>UGX {parseFloat(price || 0).toFixed(2)}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];

  return (
    <Drawer
      title="Assignment Details"
      placement="right"
      onClose={onClose}
      open={visible}
      width={window.innerWidth > 768 ? 900 : "100%"}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* BOTPC & Status */}
        <div>
          <Title level={4}>
            <Space>
              <Text code copyable style={{ wordBreak: "break-all" }}>
                {displayAssignment.botpc}
              </Text>
              <Tag color={getStatusColor(displayAssignment.status)}>
                {displayAssignment.status}
              </Tag>
            </Space>
          </Title>
        </div>

        <Divider />

        {/* Student Information */}
        <div>
          <Title level={5}>Student Information</Title>
          <Descriptions bordered column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Student Name" span={2}>
              <div style={{ wordBreak: "break-word" }}>
                {displayAssignment.student_name || "N/A"}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Student SPC">
              <Space>
                <Text style={{ wordBreak: "break-all" }}>
                  {displayAssignment.student_spc}
                </Text>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(displayAssignment.student_spc)}
                />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Class">
              <div style={{ wordBreak: "break-word" }}>
                {templateData?.class_name ||
                  displayAssignment.class_name ||
                  "N/A"}
              </div>
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Template Information */}
        <div>
          <Title level={5}>Template Information</Title>
          <Descriptions bordered column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Template Name" span={2}>
              <div style={{ wordBreak: "break-word" }}>
                {displayAssignment.template_name || templateData?.name || "N/A"}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Term">
              {templateData?.term_name || `Term ${displayAssignment.term}`}
            </Descriptions.Item>
            <Descriptions.Item label="Academic Year">
              {templateData?.acadyr || displayAssignment.acadyr}
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Payment Information */}
        <div>
          <Title level={5}>Payment Information</Title>
          <Descriptions bordered column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Total Amount">
              <Text strong>
                UGX {parseFloat(displayAssignment.total_amount || 0).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Amount Paid">
              <Text type="success">
                UGX {parseFloat(displayAssignment.amount_paid || 0).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Balance">
              <Text
                strong
                type={
                  parseFloat(displayAssignment.balance) > 0
                    ? "danger"
                    : "success"
                }
              >
                UGX {parseFloat(displayAssignment.balance || 0).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Deadline">
              {displayAssignment.payment_deadline || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Deadlines */}
        <div
          style={{
            padding: "12px 16px",
            background: "#fafafa",
            borderRadius: "4px",
            border: "1px solid #d9d9d9",
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text strong>
              Payment Deadline:{" "}
              <Text>{displayAssignment.payment_deadline || "N/A"}</Text>
            </Text>
            <Text strong>
              Pickup Deadline:{" "}
              <Text>{displayAssignment.pickup_deadline || "N/A"}</Text>
            </Text>
          </Space>
        </div>

        {/* Available Items & Selection Status */}
        <div>
          <Title level={5}>
            Available Items & Selection Status
            {mergedItems.length > 0 && (
              <Text
                type="secondary"
                style={{ fontSize: "14px", marginLeft: "10px" }}
              >
                ({mergedItems.filter((item) => item.is_selected).length} of{" "}
                {mergedItems.length} selected)
              </Text>
            )}
          </Title>
          {loadingItems ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin tip="Loading template items..." />
            </div>
          ) : mergedItems.length > 0 ? (
            <Table
              columns={itemsColumns}
              dataSource={mergedItems}
              rowKey="id"
              pagination={false}
              scroll={{ x: 900 }}
              size="small"
              rowClassName={(record) =>
                record.is_selected ? "selected-row" : ""
              }
            />
          ) : (
            <Text type="secondary">No items available in this template</Text>
          )}
        </div>

        {/* Timestamps */}
        <div
          style={{
            padding: "12px 16px",
            background: "#f5f5f5",
            borderRadius: "4px",
            fontSize: "12px",
            color: "#8c8c8c",
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">
              <strong>Created:</strong>{" "}
              {displayAssignment.created_at
                ? new Date(displayAssignment.created_at).toLocaleString()
                : "N/A"}
            </Text>
            <Text type="secondary">
              <strong>Updated:</strong>{" "}
              {displayAssignment.updated_at
                ? new Date(displayAssignment.updated_at).toLocaleString()
                : "N/A"}
            </Text>
          </Space>
        </div>
      </Space>
    </Drawer>
  );
};

export default AssignmentDetailsDrawer;
